import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { isAdmin } from '@/utils/subscription';
import api from '../api';

function downloadCsv(codes) {
  const header = ['code', 'status', 'name', 'email', 'issued_at', 'entered_at'];
  const lines = [header.join(',')];
  (codes || []).forEach((row) => {
    const cells = [
      row.code,
      row.status,
      row.display_name || '',
      row.user_email || '',
      row.issued_at || '',
      row.entered_at || '',
    ].map((value) => `"${String(value).replace(/"/g, '""')}"`);
    lines.push(cells.join(','));
  });
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'byonco-lifetime-draw-codes.csv';
  link.click();
  URL.revokeObjectURL(url);
}

export default function LifetimeDrawAdminPage() {
  const { user, loading: authLoading } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');

  const load = async () => {
    const { data } = await api.get('/api/admin/draw/lifetime');
    setSnapshot(data);
  };

  useEffect(() => {
    if (!isAdmin(user)) return;
    load().catch((err) => {
      setError(err?.response?.data?.detail || 'Could not load the draw.');
    });
  }, [user]);

  const generate = async () => {
    setBusy('generate');
    setError('');
    setNotice('');
    try {
      const { data } = await api.post('/api/admin/draw/lifetime/generate');
      setSnapshot(data);
      setNotice(`${data.codes?.length || 0} codes are in the pool.`);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not generate codes.');
    } finally {
      setBusy('');
    }
  };

  const runDraw = async () => {
    const confirmed = window.confirm(
      'This selects up to 10 entered codes at random and turns those accounts into lifetime ByOnco PRO. It can only be done once. Continue?'
    );
    if (!confirmed) return;
    setBusy('run');
    setError('');
    setNotice('');
    try {
      const { data } = await api.post('/api/admin/draw/lifetime/run');
      setSnapshot(data);
      setNotice(`Draw complete. ${data.winners_selected || 0} lifetime memberships granted.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'The draw could not be run.');
    } finally {
      setBusy('');
    }
  };

  const codes = snapshot?.codes;
  const visible = useMemo(() => {
    const rows = codes || [];
    const q = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (filter !== 'all' && row.status !== filter) return false;
      if (!q) return true;
      return [row.code, row.user_email, row.display_name, row.status]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [codes, query, filter]);

  if (!authLoading && !isAdmin(user)) {
    return <Navigate to="/app" replace />;
  }

  const drawAt = snapshot?.draw_at ? new Date(snapshot.draw_at) : null;
  const drawOpen = drawAt ? Date.now() >= drawAt.getTime() : false;
  const poolCount = codes?.length || 0;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 text-[#161616]">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-[#2f6f3e]">Admin</p>
          <h1 className="text-3xl font-light tracking-tight mt-1">Lifetime draw codes</h1>
          <p className="text-sm text-[#6d6a63] mt-2 max-w-xl leading-relaxed">
            Generate 100 codes. Each code is assigned when someone pays for the monthly plan.
            They enter it on the public page. On 1 January 2027 at 12:00 noon EST, ten active entries receive lifetime access.
          </p>
        </div>
        <Link to="/app/admin" className="text-sm underline underline-offset-4 text-[#0f3e17]">
          Back to admin
        </Link>
      </div>

      {error ? <p className="mb-4 text-sm text-red-800" role="alert">{error}</p> : null}
      {notice ? <p className="mb-4 text-sm text-[#0f3e17]" role="status">{notice}</p> : null}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          ['Pool', poolCount ? `${poolCount}` : '0'],
          ['Still open', snapshot?.codes_remaining ?? '—'],
          ['Entered', snapshot?.entries ?? 0],
          ['Winners', snapshot?.winners_selected ?? 0],
        ].map(([label, value]) => (
          <div key={label} className="rounded-2xl border border-[#e6e2d8] bg-white px-4 py-4">
            <p className="text-[11px] uppercase tracking-[0.14em] text-[#6d6a63]">{label}</p>
            <p className="text-2xl mt-1 tabular-nums">{value}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <button
          type="button"
          onClick={generate}
          disabled={busy !== '' || poolCount >= 100}
          className="rounded-full bg-[#0f3e17] text-white px-5 py-2.5 text-sm disabled:opacity-50"
        >
          {busy === 'generate' ? 'Generating…' : poolCount >= 100 ? '100 codes ready' : 'Generate 100 codes'}
        </button>
        <button
          type="button"
          onClick={() => downloadCsv(codes)}
          disabled={!poolCount}
          className="rounded-full border border-[#0f3e17] text-[#0f3e17] px-5 py-2.5 text-sm disabled:opacity-50"
        >
          Download CSV
        </button>
        <button
          type="button"
          onClick={runDraw}
          disabled={busy !== '' || snapshot?.drawn || !drawOpen}
          className="rounded-full border border-[#0f3e17] text-[#0f3e17] px-5 py-2.5 text-sm disabled:opacity-50"
        >
          {snapshot?.drawn ? 'Draw completed' : busy === 'run' ? 'Drawing…' : 'Run the draw'}
        </button>
        <a href="/lifetime-draw" className="rounded-full px-5 py-2.5 text-sm underline underline-offset-4 text-[#0f3e17]">
          Open the public page
        </a>
      </div>
      {!snapshot?.drawn && !drawOpen ? (
        <p className="text-xs text-[#6d6a63] mb-6">
          The draw button stays off until {snapshot?.draw_at_label || '1 January 2027, 12:00 noon EST'}.
        </p>
      ) : null}

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search code, name, or email"
          className="flex-1 rounded-2xl border border-[#e6e2d8] bg-white px-4 py-2.5 text-sm"
        />
        <select
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          className="rounded-2xl border border-[#e6e2d8] bg-white px-4 py-2.5 text-sm"
        >
          {['all', 'available', 'issued', 'entered', 'winner', 'not_selected', 'void'].map((item) => (
            <option key={item} value={item}>{item}</option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-[#e6e2d8] bg-white">
        <table className="min-w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-[0.12em] text-[#6d6a63]">
            <tr>
              <th className="px-4 py-3 font-medium">Code</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Member</th>
              <th className="px-4 py-3 font-medium">Issued</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-[#6d6a63]">
                  {poolCount ? 'No codes match that filter.' : 'Generate the pool to see the 100 codes.'}
                </td>
              </tr>
            ) : visible.map((row) => (
              <tr key={row.code} className="border-t border-[#e6e2d8]">
                <td className="px-4 py-3 font-mono tracking-wide">{row.code}</td>
                <td className="px-4 py-3 capitalize">{String(row.status || '').replace('_', ' ')}</td>
                <td className="px-4 py-3">
                  <div>{row.display_name || '—'}</div>
                  <div className="text-xs text-[#6d6a63]">{row.user_email || ''}</div>
                </td>
                <td className="px-4 py-3 text-xs text-[#6d6a63]">
                  {row.issued_at ? new Date(row.issued_at).toLocaleString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
